/**
 * PointerAmbient — weicher Pastell-Rauch, reagiert leicht auf die Maus.
 *
 * Aufsteigende Wisps + schwebende Nebelbanken; Cursor wirkt wie ein sanfter Windstoß.
 * Nur Desktop; reduced-motion → aus.
 */
import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import useIsFinePointer from "@/lib/useIsFinePointer";

const SPRING = { stiffness: 42, damping: 24, mass: 0.85 };
const SPRING_SLOW = { stiffness: 18, damping: 26, mass: 1.15 };

const RISING_WISPS = [
  {
    left: "6%",
    width: 190,
    height: 300,
    tone: "from-roseMilk/22 via-lavenderFog/14",
    peak: 0.2,
    duration: 11,
    delay: 0,
    parallax: 38,
    drift: 14,
  },
  {
    left: "22%",
    width: 160,
    height: 260,
    tone: "from-lavenderFog/18 via-sageMint/10",
    peak: 0.16,
    duration: 13,
    delay: 2.2,
    parallax: -28,
    drift: -10,
  },
  {
    left: "42%",
    width: 210,
    height: 330,
    tone: "from-roseMilk/20 via-butterCream/12",
    peak: 0.18,
    duration: 12,
    delay: 4.5,
    parallax: 32,
    drift: 12,
  },
  {
    left: "62%",
    width: 175,
    height: 280,
    tone: "from-lavenderFog/20 via-roseMilk/11",
    peak: 0.17,
    duration: 14,
    delay: 1.1,
    parallax: -34,
    drift: -16,
  },
  {
    left: "78%",
    width: 200,
    height: 310,
    tone: "from-sageMint/14 via-lavenderFog/12",
    peak: 0.15,
    duration: 12.5,
    delay: 3.8,
    parallax: 26,
    drift: 11,
  },
  {
    left: "90%",
    width: 150,
    height: 240,
    tone: "from-roseMilk/18 via-lavenderFog/10",
    peak: 0.14,
    duration: 15,
    delay: 5.6,
    parallax: -22,
    drift: -9,
  },
];

const FLOATING_BANKS = [
  {
    top: "18%",
    left: "10%",
    width: "42%",
    height: "28%",
    tone: "from-lavenderFog/16 via-roseMilk/8",
    parallaxX: 48,
    parallaxY: 22,
    duration: 16,
  },
  {
    top: "52%",
    left: "48%",
    width: "38%",
    height: "26%",
    tone: "from-roseMilk/14 via-sageMint/7",
    parallaxX: -40,
    parallaxY: -18,
    duration: 19,
  },
];

export const PointerAmbient = () => {
  const fine = useIsFinePointer();
  const reduced = usePreferStaticUI();

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, SPRING);
  const sy = useSpring(my, SPRING);
  const sxSlow = useSpring(mx, SPRING_SLOW);
  const sySlow = useSpring(my, SPRING_SLOW);

  const windX = useTransform(sx, [0, 1], [-52, 52]);
  const windY = useTransform(sy, [0, 1], [-18, 18]);
  const puffLeft = useTransform(sx, (v) => `${v * 100}%`);
  const puffTop = useTransform(sy, (v) => `${v * 100}%`);
  const puffSlowLeft = useTransform(sxSlow, (v) => `${v * 100}%`);
  const puffSlowTop = useTransform(sySlow, (v) => `${v * 100}%`);

  useEffect(() => {
    if (!fine || reduced) return undefined;

    const onMove = (e) => {
      mx.set(e.clientX / window.innerWidth);
      my.set(e.clientY / window.innerHeight);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [fine, reduced, mx, my]);

  if (!fine || reduced) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      aria-hidden="true"
      data-testid="pointer-ambient"
    >
      {/* Schwebende Nebelbanken — langsam, mausparallax */}
      {FLOATING_BANKS.map((bank, i) => (
        <FloatingBank key={i} bank={bank} sx={sx} sy={sy} />
      ))}

      {/* Aufsteigender Rauch vom unteren Rand */}
      {RISING_WISPS.map((wisp, i) => (
        <RisingWisp key={i} wisp={wisp} sx={sx} />
      ))}

      {/* Cursor-Rauch — träge, weiche Wolke */}
      <motion.div
        className="absolute -translate-x-1/2 -translate-y-1/2 h-[min(52vw,380px)] w-[min(64vw,460px)] rounded-[45%] blur-[92px] opacity-[0.09]"
        style={{
          left: puffSlowLeft,
          top: puffSlowTop,
          background:
            "radial-gradient(ellipse 80% 70% at 50% 55%, rgba(232,225,247,0.7) 0%, rgba(250,236,238,0.35) 40%, transparent 72%)",
        }}
        animate={{
          scale: [1, 1.08, 0.96, 1],
          rotate: [-2, 3, -1, -2],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Cursor-Kern — leichter Dampf direkt an der Maus */}
      <motion.div
        className="absolute -translate-x-1/2 -translate-y-1/2 h-[min(36vw,260px)] w-[min(44vw,320px)] rounded-[40%] blur-[72px] opacity-[0.11]"
        style={{
          left: puffLeft,
          top: puffTop,
          x: windX,
          y: windY,
          background:
            "radial-gradient(ellipse 75% 65% at 50% 50%, rgba(250,236,238,0.85) 0%, rgba(158,74,98,0.08) 28%, transparent 68%)",
        }}
        animate={{
          scale: [0.92, 1.06, 1, 0.94],
          rotate: [0, 4, -3, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
};

const FloatingBank = ({ bank, sx, sy }) => {
  const px = useTransform(sx, [0, 1], [-bank.parallaxX, bank.parallaxX]);
  const py = useTransform(sy, [0, 1], [-bank.parallaxY, bank.parallaxY]);

  return (
    <motion.div
      className="absolute"
      style={{
        top: bank.top,
        left: bank.left,
        width: bank.width,
        height: bank.height,
        x: px,
        y: py,
      }}
    >
      <motion.div
        className={`h-full w-full rounded-[50%] bg-gradient-to-br ${bank.tone} to-transparent blur-[80px]`}
        animate={{
          x: [0, 18, -12, 0],
          y: [0, -10, 8, 0],
          scale: [1, 1.06, 0.97, 1],
          opacity: [0.55, 0.75, 0.6, 0.55],
        }}
        transition={{ duration: bank.duration, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.div>
  );
};

const RisingWisp = ({ wisp, sx }) => {
  const parallax = useTransform(sx, [0, 1], [-wisp.parallax, wisp.parallax]);

  return (
    <motion.div
      className="absolute bottom-[-14%]"
      style={{
        left: wisp.left,
        marginLeft: -wisp.width / 2,
        x: parallax,
      }}
    >
      <motion.div
        className={`rounded-[50%] bg-gradient-to-t ${wisp.tone} to-transparent blur-3xl`}
        style={{ width: wisp.width, height: wisp.height }}
        animate={{
          y: [0, -140, -320],
          x: [0, wisp.drift, -wisp.drift * 0.6, 0],
          opacity: [0, wisp.peak, 0],
          scale: [0.65, 1.12, 1.4],
          rotate: [0, 4, -3, 0],
        }}
        transition={{
          duration: wisp.duration,
          repeat: Infinity,
          delay: wisp.delay,
          ease: "easeOut",
        }}
      />
    </motion.div>
  );
};

export default PointerAmbient;
